import { v4 as uuidv4 } from "uuid";
import { getExtension, removeExtension } from "../src/taskpane/components/helpers/stringHelper";
import { FileFolderNameExtensions } from "../src/taskpane/services/FileFolderNameExtensions";
import { validSaveAsPdfExtentions } from "../src/taskpane/config/constants";
import { IFileToUpload } from "../src/taskpane/interfaces/IFileToUpload";
import { logger } from "./logger";

export interface IOutlookDroppedItem {
    name: string;
    type: string;
    fileContent: Blob;
}

export interface IOutlookFileBuildResult {
    fileToUpload: IFileToUpload;
    file: File;
}

// Turns Office.js DragAndDropEvent drop payloads into isFileSystemEntry uploads,
// mirroring DragAndDropHelper.getFileToUpload so the existing upload pipeline can
// consume them unchanged. Pure and side-effect free (aside from logging skips).
export function buildOutlookFileUploads(
    items: IOutlookDroppedItem[],
    noSubjectFallback: string
): IOutlookFileBuildResult[] {
    const results: IOutlookFileBuildResult[] = [];
    if (!Array.isArray(items)) {
        return results;
    }

    for (const item of items) {
        try {
            // Content guard: never stage/upload an item without real bytes. Rejects a
            // missing/zero-size Blob (guards against a regression of the office-js
            // #6370-class "no content" behavior) and any non-Blob shape lacking a numeric
            // `size` (duck-typed rather than `instanceof Blob`, which is unreliable across
            // the Office host's realm boundary).
            if (!item || !item.fileContent || typeof item.fileContent.size !== "number" || item.fileContent.size === 0) {
                logger(
                    new Error(`Skipped dropped item with empty content: ${item?.name ?? "(unknown)"}`),
                    "outlookDroppedItems.ts (buildOutlookFileUploads)"
                );
                continue;
            }

            const extension: string = getExtension(item.name)?.trim().toLowerCase() ?? "";
            // formatEmptyValue = false so an empty/whitespace base returns "" (not a
            // timestamp), letting us apply the localized NoSubject fallback instead.
            const sanitizedBase = FileFolderNameExtensions.EnsureValidName(removeExtension(item.name), true, false);
            const name: string = sanitizedBase && sanitizedBase.trim().length > 0 ? sanitizedBase : noSubjectFallback;

            const fileName: string = extension ? `${name}.${extension}` : name;
            const file: File = new File([item.fileContent], fileName, {
                type: item.fileContent.type || item.type || ""
            });

            const id: string = uuidv4();
            const fileToUpload: IFileToUpload = {
                id,
                ewsId: null,
                name,
                extension,
                isAttachment: false,
                isSelected: true,
                isDuplicate: false,
                originalName: name,
                lastModifiedDateTime: new Date(file.lastModified).toISOString(),
                isFileSystemEntry: true,
                canSaveAsPDF: validSaveAsPdfExtentions.some(ext => ext === extension)
            };

            results.push({ fileToUpload, file });
        } catch (error) {
            logger(error, "outlookDroppedItems.ts (buildOutlookFileUploads)");
        }
    }

    return results;
}
